import { useEffect, useMemo, useRef, useState } from 'react';
import { snoozePresets, type SnoozePreset } from './snoozeTimes';
import { useDialogFocus } from '@/lib/useDialogFocus';

interface Props {
  anchorEl: HTMLElement | null;
  onSnooze: (untilIso: string) => void;
  onClose: () => void;
}

/**
 * Anchored popover with snooze presets + custom datetime-local input.
 * Bound to keyboard digits 1-5 for the presets via parent's useHotkeys.
 */
export function SnoozePopover({ anchorEl, onSnooze, onClose }: Props) {
  const [presets] = useState<SnoozePreset[]>(() => snoozePresets());
  const [custom, setCustom] = useState('');
  // Combine the existing outside-click ref with focus-trap behavior. Same ref
  // serves both: the panel container.
  const dialogRef = useDialogFocus<HTMLDivElement>();
  const outsideRef = useRef<HTMLDivElement | null>(null);
  function setRefs(el: HTMLDivElement | null) {
    dialogRef.current = el;
    outsideRef.current = el;
  }
  // Anchored position from the trigger button. Falls back to the previous
  // top-right corner if no anchor available (e.g. opened via global 's'
  // hotkey with no selected row).
  const pos = useMemo(() => {
    if (!anchorEl) return null;
    const r = anchorEl.getBoundingClientRect();
    // Position below the trigger, right-aligned to the trigger's right edge.
    return { top: r.bottom + 6, right: window.innerWidth - r.right };
  }, [anchorEl]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!outsideRef.current) return;
      if (outsideRef.current.contains(e.target as Node)) return;
      if (anchorEl && anchorEl.contains(e.target as Node)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchorEl, onClose]);

  return (
    <div
      ref={setRefs}
      role="dialog"
      aria-modal="true"
      aria-label="Snooze options"
      style={
        pos
          ? {
              position: 'fixed',
              top: pos.top,
              right: pos.right,
              // Origin-aware scale: popover scales OUT of its trigger
              // (top-right corner — same edge the trigger sits on). Per
              // Emil: tooltips/popovers should never scale from center;
              // they belong to their anchor.
              transformOrigin: 'top right',
            }
          : {
              position: 'absolute',
              top: '5rem',
              right: '1.5rem',
              transformOrigin: 'top right',
            }
      }
      className="z-30 w-72 space-y-1 rounded-lg border border-border bg-popover p-1 shadow-2xl motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-150 motion-safe:ease-out"
    >
      <div className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        Snooze until
      </div>
      {presets.map((p, i) => (
        <button
          key={p.key}
          onClick={() => onSnooze(p.iso)}
          data-autofocus={i === 0 ? '' : undefined}
          className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-accent"
        >
          <span>{p.label}</span>
          <span className="font-mono text-[10px] text-muted-foreground">
            {p.shortcut ? `[${p.shortcut}]` : ''}
          </span>
        </button>
      ))}
      <div className="my-1 h-px bg-border" />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (custom) onSnooze(new Date(custom).toISOString());
        }}
        className="space-y-1 px-3 py-2"
      >
        <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Custom
        </label>
        <div className="flex gap-1.5">
          <input
            type="datetime-local"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            className="block min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 text-xs"
          />
          <button
            type="submit"
            disabled={!custom}
            className="rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground disabled:opacity-60"
          >
            Set
          </button>
        </div>
      </form>
    </div>
  );
}
